import React, { useEffect, useMemo } from "react";

/**
 * Single-file, preview-safe article page.
 * - No external imports (prevents "File not found" errors in the editor).
 * - Paste your legacy HTML into `legacyHtml` and it will render inside the site frame.
 * - Strips <head>/<style>/<script>/<link> so it doesn't break your app styling.
 * - Optional image rewrite: /images/... -> /images/articles/...
 */

const BRAND = {
  white: "#ffffff",
  charcoal: "#0b0b0f",
  gray: "#6b7280",
  burgundy: "#800020",
  gold: "#b08d57",
  lilac: "#a78bfa", // sponsor-only
};

function Rule({ className = "" }) {
  return <div className={`h-px w-full bg-black/10 ${className}`} />;
}

function Box({ children, className = "" }) {
  return <div className={`border border-black/15 bg-white ${className}`}>{children}</div>;
}

function SafeIcon() {
  // Preview-safe icon (no asset import). Replace with your real logo icon in your app.
  return (
    <svg width="36" height="36" viewBox="0 0 64 64" aria-hidden="true">
      <rect x="6" y="6" width="52" height="52" fill="#0b0b0f" />
      <rect x="12" y="12" width="40" height="40" fill="#ffffff" />
      <path d="M18 42V22h10c7 0 12 4 12 10s-5 10-12 10H18zm6-5h4c4 0 7-2 7-5s-3-5-7-5h-4v10z" fill="#0b0b0f" />
    </svg>
  );
}

function SiteHeader() {
  return (
    <header className="sticky top-0 z-40 bg-white">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <div className="pt-5 sm:pt-6">
          <div className="flex items-center justify-between gap-3">
            <div className="hidden sm:flex items-center gap-4">
              <a
                href="/"
                className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-600 hover:text-black"
              >
                Music × Culture
              </a>
              <a
                href="/"
                className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-600 hover:text-black"
              >
                Art × Fashion
              </a>
            </div>
            <div className="flex items-center gap-4">
              <a
                href="/ams"
                className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-600 hover:text-black"
              >
                AMS
              </a>
              <a
                href="/login"
                className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-600 hover:text-black"
              >
                Login
              </a>
            </div>
          </div>

          <Rule className="mt-4" />

          <div className="grid grid-cols-1 items-end gap-3 py-4 sm:grid-cols-[1fr_auto_1fr]">
            <div className="hidden sm:block">
              <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-500">
                Africa Loud Suite
              </div>
              <div className="mt-1 text-xs text-neutral-600">Culture intelligence → creative execution</div>
            </div>

            <a href="/" className="mx-auto flex items-center gap-3 text-center" aria-label="Go to Home">
              <SafeIcon />
              <div>
                <div className="font-serif text-4xl tracking-tight text-neutral-950 sm:text-5xl">Africa Loud</div>
                <div className="-mt-1 text-[11px] font-semibold tracking-[0.25em] uppercase text-neutral-600">
                  Editorial
                </div>
              </div>
            </a>

            <div className="hidden justify-self-end sm:block">
              <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-500">Weekly drops</div>
              <div className="mt-1 text-xs text-neutral-600">Signals · Cities · Strategy</div>
            </div>
          </div>

          <Rule />

          <nav className="flex flex-wrap items-center justify-between gap-2 py-2">
            <div className="flex items-center gap-2">
              <a
                href="/"
                className="px-3 py-2 text-[11px] font-semibold tracking-[0.18em] uppercase transition bg-white text-black hover:bg-black/5"
              >
                Home
              </a>
              <a
                href="/"
                className="px-3 py-2 text-[11px] font-semibold tracking-[0.18em] uppercase transition bg-black text-white"
              >
                Culture × Music
              </a>
              <a
                href="/"
                className="px-3 py-2 text-[11px] font-semibold tracking-[0.18em] uppercase transition bg-white text-black hover:bg-black/5"
              >
                Art × Fashion
              </a>
            </div>

            <div className="hidden items-center gap-2 sm:flex">
              <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-500">Subscribe</div>
              <div className="h-3 w-px bg-black/15" />
              <div className="text-[11px] text-neutral-600">One email · one week</div>
            </div>
          </nav>

          <Rule />
        </div>
      </div>
    </header>
  );
}

function SiteFooter() {
  const links = [
    { label: "Instagram", href: "https://instagram.com/africaloudmag", external: true },
    { label: "Twitter", href: "https://twitter.com/africaloudmag", external: true },
    { label: "TikTok", href: "https://tiktok.com/@africaloudmag", external: true },
    { label: "Terms", href: "/terms" },
    { label: "Privacy", href: "/terms" },
    { label: "Collab", href: "/collab" },
  ];

  return (
    <footer className="mt-12 bg-neutral-800 px-5 py-6">
      <div className="mx-auto max-w-6xl">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-sm font-semibold text-white">Africa Loud</div>
          <div className="flex flex-wrap gap-x-5 gap-y-2">
            {links.map((l) => (
              <a
                key={l.label}
                href={l.href}
                target={l.external ? "_blank" : undefined}
                rel={l.external ? "noopener noreferrer" : undefined}
                className="text-[11px] font-semibold tracking-[0.18em] uppercase text-white/80 hover:text-white"
              >
                {l.label}
              </a>
            ))}
          </div>
        </div>
        <div className="mt-4 text-xs text-white/70">© 2026 Africa Loud</div>
      </div>
    </footer>
  );
}

function SponsorStrip() {
  return (
    <div className="mt-6">
      <Box className="p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-500">Partner</div>
            <div className="mt-1 font-serif text-xl tracking-tight text-neutral-950">ON THIRD - Premium Luxury</div>
            <div className="mt-1 text-sm text-neutral-700">Shop the scent with a 10% off offer via the partner link.</div>
          </div>
          <a
            href="https://onthird.co.za/"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center justify-center border border-black px-5 py-3 text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-950 hover:bg-black hover:text-white"
            style={{ borderColor: BRAND.lilac }}
          >
            Shop the scent 
            <span className="ml-2">→</span>
          </a>
        </div>
      </Box>
    </div>
  );
}

function stripLegacyHtml(html) {
  // Remove wrappers/head assets/scripts so legacy pages don’t hijack styling.
  let out = html || "";

  // Remove doctype/html/head/body tags entirely
  out = out
    .replace(/<!doctype[^>]*>/gi, "")
    .replace(/<html[^>]*>/gi, "")
    .replace(/<\/html>/gi, "")
    .replace(/<head[\s\S]*?<\/head>/gi, "")
    .replace(/<body[^>]*>/gi, "")
    .replace(/<\/body>/gi, "");

  // Remove scripts/styles/links (common in pasted legacy pages)
  out = out
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/<style[\s\S]*?<\/style>/gi, "")
    .replace(/<link[^>]*>/gi, "");

  // Kill on* handlers (onclick, onload, etc.) to avoid runtime errors
  out = out.replace(/\son\w+\s*=\s*"[^"]*"/gi, "");
  out = out.replace(/\son\w+\s*=\s*'[^']*'/gi, "");

  return out.trim();
}

function rewriteLegacyImages(html, prefix = "/images/articles/") {
  // /images/foo.png -> /images/articles/foo.png
  return (html || "").replace(/(src=\s*["'])\/?images\//gi, `$1${prefix}`);
}

function normalizeAnchors(html) {
  // If you have legacy links like /echo/articles/x.html, you can remap them here.
  // Current behavior: keep links as-is.
  return html || "";
}

function ArticlePageTemplate({
  seoTitle,
  seoDescription,
  canonicalPath,
  heroTitle,
  heroByline,
  heroImage,
  legacyHtml,
  stripStyles = true,
  rewriteImages = true,
}) {
  const preparedHtml = useMemo(() => {
    let out = legacyHtml || "";
    if (stripStyles) out = stripLegacyHtml(out);
    if (rewriteImages) out = rewriteLegacyImages(out);
    out = normalizeAnchors(out);
    return out;
  }, [legacyHtml, stripStyles, rewriteImages]);

  useEffect(() => {
    // Minimal SEO in preview: update title/description.
    if (typeof document === "undefined") return;
    if (seoTitle) document.title = seoTitle;

    if (seoDescription) {
      let tag = document.querySelector('meta[name="description"]');
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("name", "description");
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", seoDescription);
    }

    // Canonical (safe in most apps; in this preview it’s fine too)
    if (canonicalPath) {
      let canon = document.querySelector('link[rel="canonical"]');
      if (!canon) {
        canon = document.createElement("link");
        canon.setAttribute("rel", "canonical");
        document.head.appendChild(canon);
      }
      canon.setAttribute("href", canonicalPath);
    }
  }, [seoTitle, seoDescription, canonicalPath]);

  return (
    <div className="min-h-screen bg-white text-neutral-950">
      <SiteHeader />

      <main className="mx-auto max-w-6xl px-4 sm:px-6">
        <div className="pt-8">
          <div className="flex items-center gap-3">
            <div className="h-2 w-2" style={{ backgroundColor: BRAND.burgundy }} />
            <div className="text-[11px] font-semibold tracking-[0.22em] uppercase text-neutral-600">Culture × Music</div>
          </div>

          {/* Hero */}
          <div className="mt-6 grid gap-4 lg:grid-cols-[1.2fr_0.8fr]">
            <Box className="p-6 sm:p-8">
              <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-500">Feature</div>
              <h1 className="mt-4 font-serif text-4xl leading-[1.05] tracking-tight sm:text-6xl">{heroTitle}</h1>
              {heroByline ? <div className="mt-3 text-sm text-neutral-600">{heroByline}</div> : null}
              <div className="mt-6">
                <Rule />
                <div className="mt-3 h-1 w-full" style={{ backgroundColor: BRAND.burgundy }} />
              </div>
            </Box>

            <Box className="relative overflow-hidden">
              <div
                className="h-full min-h-[280px] w-full"
                style={{
                  backgroundImage: `url('${heroImage}')`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
              />
              <div className="absolute bottom-0 left-0 right-0">
                <div className="bg-white/92 p-5 backdrop-blur-sm">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-500">Read</div>
                      <div className="mt-1 text-base font-semibold text-neutral-950">Africa Loud</div>
                    </div>
                    <div className="text-[11px] font-semibold tracking-[0.18em] uppercase" style={{ color: BRAND.burgundy }}>
                      Director's pick
                    </div>
                  </div>
                </div>
              </div>
            </Box>
          </div>

          {/* Sponsor stays on all pages */}
          <SponsorStrip />

          {/* Article body */}
          <div className="mt-8 pb-12">
            <Box className="p-6 sm:p-10">
              <article
                className="max-w-none text-neutral-900 leading-relaxed [&_p]:mb-5 [&_h2]:mt-10 [&_h2]:mb-4 [&_h2]:font-serif [&_h2]:text-3xl [&_h2]:tracking-tight [&_h3]:mt-8 [&_h3]:mb-3 [&_h3]:font-serif [&_h3]:text-2xl [&_h3]:tracking-tight [&_ul]:my-5 [&_ul]:pl-6 [&_li]:mb-2 [&_a]:underline [&_a]:decoration-black/30 hover:[&_a]:decoration-black"
                dangerouslySetInnerHTML={{ __html: preparedHtml }}
              />
            </Box>
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}

// --- “Tests” (runs in dev/preview) ---
function runTemplateSanityTests() {
  // eslint-disable-next-line no-console
  console.assert(stripLegacyHtml("<script>alert(1)</script><p>ok</p>").includes("<p>ok</p>"), "stripLegacyHtml should remove script");
  // eslint-disable-next-line no-console
  console.assert(!stripLegacyHtml("<style>h1{}</style><p>ok</p>").includes("<style"), "stripLegacyHtml should remove style");
  // eslint-disable-next-line no-console
  console.assert(
    rewriteLegacyImages('<img src="/images/a.png" />').includes('/images/articles/a.png'),
    "rewriteLegacyImages should rewrite /images/"
  );
}

export default function BeyonceArticle() {
  useEffect(() => {
    if (typeof window !== "undefined") runTemplateSanityTests();
  }, []);

  return (
    <ArticlePageTemplate
      seoTitle="The Rise of Tyla:"
      seoDescription="Empowering the uppity African in Today’s World"
      canonicalPath="/culture-music/the-rise-of-tyla"
      heroTitle="The Rise of Tyla:"
      heroByline="Written By Analo. S • Africa Loud Culture Desk"
      
      heroImage="/images/articles/#"
      stripStyles={true}
      rewriteImages={true}
      legacyHtml={`

      
  <article>
    <section>
      <figure>
        <!-- Tyla 'Water' performance image (YouTube thumbnail) -->
        <img 
          src="https://i.ytimg.com/vi/-DkJMa7QkGY/maxresdefault.jpg" 
          alt="Tyla performing the Amapiano hit 'Water' on stage" 
          width="800" 
          height="450"
        >
        <figcaption>Tyla performing <em>Water</em> on a global stage. </figcaption>
      </figure>

      <p><strong>No matter how long the winter, spring is sure to follow – Guinea Proverb</strong></p>

      <p>
        The warm rays of spring have finally shone on Amapiano as it is getting the global recognition it deserves. Today's audiences crave diverse sounds and fresh experiences—something unique and unexplored. Enter African artists, who consistently push boundaries by blending genres to create something new, vibrant, and fresh. A perfect example is South African singer and songwriter <strong>Tyla</strong>.
      </p>

      <h2>Water, the song that put Tyla on the map</h2>
      <p>
        2024 has truly been Tyla's year, and it all started with her hit song <em>Water</em>. This fun and catchy anthem combined Amapiano and pop elements into something high-energy yet original. It resonated with both local and international audiences, earning her accolades such as:
      </p>
      <ul>
        <li><strong>The inaugural Best African Music Performance Award</strong> at the 66th Grammy Awards—Tyla made history as both the first-ever winner and the youngest African artist to win a Grammy at 22 years old (OkayAfrica, 2024).</li>
        <li><strong>Top Afrobeats Song</strong> and <strong>Top Afrobeats Artist</strong> at the 2024 Billboard Music Awards (8 nominations across 6 categories).</li>
        <li><strong>Best New Artist</strong> and <strong>Best International Act</strong> at the 2024 BET Awards (Premium Nigeria).</li>
        <li><strong>Best Afrobeats</strong>, <strong>Best African Act</strong>, and <strong>Best R&amp;B</strong> at the 2024 MTV EMAs.</li>
        <li><strong>Best Afrobeats</strong> at the 2024 MTV VMAs.</li>
        <li><strong>Best Newcomer of the Year</strong>, <strong>International Achievement</strong>, <strong>Female Artist of the Year</strong>, and <strong>Best Pop Album</strong> at the 2024 South African Music Awards (SAMAs).</li>
      </ul>
      <p>
        According to OkayAfrica, Tyla, Burna Boy, and Arya Starr dominated Spotify streams in 2024 among African artists. Her song <em>Water</em> amassed over 800 million streams on Spotify and 284 million views on YouTube.
      </p>
    </section>

    <section>
      <figure>
        <!-- Tyla holding her Grammy Award (Instagram photo) -->
        <img 
          src="https://www.instagram.com/p/C29D5JgLEmm/media/?size=l" 
          alt="Tyla holding the Best African Music Performance Grammy Award" 
          width="500" 
          height="535"
        >
        <figcaption>Tyla wins the inaugural Best African Music Performance Award at the 66th Grammy Awards. </figcaption>
      </figure>

      <h2>Exciting collaborations</h2>
      <p>
        In March 2024, Tyla dropped her highly anticipated self-titled album featuring collaborations with icons like Gunna, Tems, and Travis Scott. Partnering with both African and African-American heavyweights has broadened Amapiano’s reach and drawn new listeners. She later released a deluxe edition containing anthems like “Push 2 Start” and the Tony Duardo-produced track “Shake Ah.”
      </p>
      <p>
        Beyond music, Tyla secured high-profile fashion collaborations. In 2024, she became the face of GAP’s global linen campaign, merging music, dance, and fashion to promote their linen collection. 
      </p>

      <figure>
        <!-- Tyla for GAP linen campaign -->
        <img 
          src="/images/TylaxGAP.jpg" 
          alt="Tyla starring in GAP’s Spring 2024 linen campaign" 
          width="800" 
          height="450"
        >
     <p>Image by GAP</p>
        <figcaption>Tyla starring in GAP’s Spring 2024 linen campaign. </figcaption>
      </figure>
    </section>

    <section>
      <h2>What does this mean for the future of African music and artists?</h2>
      <p>
        Tyla is a rising star not only for herself but for the broader African music ecosystem. The introduction of the <strong>Best African Music Performance</strong> category at the Grammys created a new avenue for African artists to gain global recognition. By winning that inaugural Grammy, Tyla has set a precedent for future musicians to be celebrated on international stages.
      </p>
      <p>
        Collaborations between African and African-American artists help Amapiano evolve by incorporating varied sounds and beats. This unity amplifies African music globally, forging stronger connections between artists on the continent and the diaspora.
      </p>
      <p>
        However, challenges remain. Tyla’s multiple Afrobeat awards highlight a tendency to lump all African genres under “Afrobeats,” despite the diversity of sounds like Amapiano, Gqom, and Afro-Pop. Tyla herself noted at the VMAs: 
      </p>
      <blockquote>
        “This is just so special, but also bittersweet because I know there's a tendency to group all African artists under Afrobeats. It's a thing, and even though Afrobeats has run things and has opened so many doors for us, African music is so diverse, it is more than just Afrobeats.”
      </blockquote>
      <p>Read more:<a href="/echo/is-tyla-afrobeat.html">Is Tyla Afrobeat?</a></p>
      <p>
        As Amapiano garners unprecedented global attention, the hope is that individual genres will receive proper distinction and celebration over time.
      </p>

    </section>
    <img src="/images/tylaxSA.jpg"
    alt=" Tyla is proudly South African by posing in front of a South African flag."
    width="600" 
          height="450">
          <p></p>
    <section>
        <p>
  Don't be discouraged just yet! Because Amapiano is getting global recognition at this magnitude for the first time, it is normal that there will be some hiccups at the beginning. There is still hope that as time goes on and more recognition is garnered, Amapiano and other African music genres will get the individual recognition and celebration they deserve.
</p>

      <h2>Final Takeaway</h2>
      <p>
        Tyla is a phenomenal artist who has risen to become the face of African pop. Her hit song <em>Water</em> has driven global adoration, spotlighted Amapiano, and created future opportunities for African artists. Her collaborations promise to further unify African music with the diaspora and foster continuous evolution. Despite initial mislabeling of genres, the trajectory for African music—and African artists in general—appears brighter than ever.
      </p>
    </section>
  </article>
 `}
    />
  );
}
